import { forwardRef, type ReactNode, type SelectHTMLAttributes } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps
  extends SelectHTMLAttributes<HTMLSelectElement> {
  /**
   * Lista declarativa de opciones. Si se pasan, los children se ignoran.
   * Si no, el caller debe pasar `<option>` como children manualmente —
   * útil cuando hace falta `<optgroup>` o renderizar custom.
   */
  options?: SelectOption[];
  error?: boolean;
  /**
   * Placeholder. Renderiza una option con `value=""` deshabilitada como primer
   * item del select. Si está presente, el caller debe asegurarse de que el
   * valor inicial sea "" para que el placeholder se muestre.
   */
  placeholder?: string;
  children?: ReactNode;
}

/**
 * Select estilizado contra `.select` legacy (tokens.css). NO es un combobox
 * con typeahead — para eso usar `SearchableSelect`. Este wrapper sirve para
 * casos con < 10 opciones (currency, role, status, etc.).
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  function Select(
    { options, error, placeholder, className, children, ...rest },
    ref,
  ) {
    const classes = ['select'];
    if (className) classes.push(className);
    return (
      <select
        ref={ref}
        className={classes.join(' ')}
        data-error={error ? 'true' : undefined}
        {...rest}
      >
        {placeholder ? (
          <option value="" disabled>
            {placeholder}
          </option>
        ) : null}
        {options
          ? options.map((opt) => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))
          : children}
      </select>
    );
  },
);
